/**
 * useStorefront — React Query hooks for the customer catalogue.
 * All are scoped by `collegeId`; they stay disabled until a college is chosen.
 */

import { useQuery, useQueries } from '@tanstack/react-query';
import { storefrontApi } from '@/api/storefront.api';

export function useColleges() {
  return useQuery({ queryKey: ['sf', 'colleges'], queryFn: storefrontApi.colleges });
}

export function useCategories(collegeId) {
  return useQuery({
    queryKey: ['sf', 'categories', collegeId],
    queryFn: () => storefrontApi.categories(collegeId),
    enabled: Boolean(collegeId),
  });
}

export function useCoupons(collegeId) {
  return useQuery({
    queryKey: ['sf', 'coupons', collegeId],
    queryFn: () => storefrontApi.coupons(collegeId),
    enabled: Boolean(collegeId),
  });
}

export function useTopSelling(collegeId) {
  return useQuery({
    queryKey: ['sf', 'topSelling', collegeId],
    queryFn: () => storefrontApi.topSelling(collegeId),
    enabled: Boolean(collegeId),
  });
}

/**
 * One products query per category, in parallel.
 *
 * `useQueries` rather than a loop of `useQuery`, because the number of
 * categories is data — it changes when an admin adds one — and hooks cannot be
 * called conditionally. Each keeps its own cache entry, so opening a category
 * page afterwards is already warm.
 */
export function useCategoryRails(collegeId, categories = [], perRail = 6) {
  return useQueries({
    queries: categories.map((category) => ({
      queryKey: ['sf', 'products', collegeId, { category: category._id, limit: perRail }],
      queryFn: () =>
        storefrontApi.products({ college: collegeId, category: category._id, limit: perRail }),
      enabled: Boolean(collegeId),
    })),
    combine: (results) =>
      categories
        .map((category, index) => ({
          category,
          products: results[index]?.data?.items ?? [],
          isLoading: results[index]?.isLoading ?? false,
        }))
        // A category with nothing in it should not leave an empty heading.
        .filter((rail) => rail.isLoading || rail.products.length > 0),
  });
}

export function useDeliveryLocations(collegeId) {
  return useQuery({
    queryKey: ['sf', 'deliveryLocations', collegeId],
    queryFn: () => storefrontApi.deliveryLocations(collegeId),
    enabled: Boolean(collegeId),
  });
}

export function useProducts({ collegeId, search, category, page = 1, limit = 20 } = {}) {
  return useQuery({
    queryKey: ['sf', 'products', collegeId, { search, category, page, limit }],
    queryFn: () => storefrontApi.products({ college: collegeId, search, category, page, limit }),
    enabled: Boolean(collegeId),
    placeholderData: (prev) => prev, // keep old results visible while refetching
  });
}

export function useProduct(slug, collegeId) {
  return useQuery({
    queryKey: ['sf', 'product', collegeId, slug],
    queryFn: () => storefrontApi.product(slug, collegeId),
    enabled: Boolean(slug && collegeId),
  });
}
